<template>
  <div class="condition-button-container">
    <button
      class="button condition-button"
      @click="openConfig"
      :class="{ 'config-active': isOpen, invalid: condition?.invalid }"
      :disabled="disabled"
    >
      <Icon :icon="getIconComponent(condition!)" />
      <span class="description">{{ manager.getFormattedDescription(condition!) }}</span>
      <div class="delete-icon">
        <a class="delete is-small" @click.stop="deleteCondition()"></a>
      </div>
    </button>

    <div class="config-wrapper" @mousedown="onMouseDownInside" @mouseup="onMouseUpInside">
      <ConditionConfigurator
        v-if="isOpen"
        v-click-away="closeConfig"
        :value="condition!"
        :manager="manager"
        @updated="updateCondition"
        @completed="onConfigCompleted"
      >
      </ConditionConfigurator>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ConditionViewModel } from './models/conditionViewModel';
import { IConditionsManager } from './models/conditionsManager';
import ConditionConfigurator from './ConditionConfigurator.vue';
import Icon from '../../icons/Icon.vue';
import { PropType, ref, watch, onBeforeUnmount } from 'vue';
import { IconType } from '@/shared/icons/icons';
import { ConditionPickerOption } from '@shared/components/conditions/models/conditions';
import useVuelidate from '@vuelidate/core';

const props = defineProps({
  value: {
    type: Object as PropType<ConditionViewModel>,
    required: true,
  },
  manager: {
    type: Object as PropType<IConditionsManager>,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  (e: 'updated', condition: ConditionViewModel): void;
  (e: 'deleted'): void;
  (e: 'config-opened'): void;
  (e: 'config-closed'): void;
}>();

const condition = ref<ConditionViewModel>();
const isOpen = ref(false);
const deleted = ref(false);
const allowClickAway = ref(true);
const $v = useVuelidate();

watch(
  () => props.value,
  (value: ConditionViewModel) => {
    condition.value = value;
    const isActive = condition.value.active || false;
    if (isOpen.value !== isActive) {
      emit(isActive ? 'config-opened' : 'config-closed');
    }
    isOpen.value = isActive;
    checkConditionStillValid();
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  if (!deleted.value && isOpen.value) {
    closeConfig();
  }
});

function getIconComponent(condition: ConditionViewModel): IconType {
  const pickerOption = props.manager.conditions.value.find((c: ConditionPickerOption) => c.id === condition.attribute);
  if (pickerOption && pickerOption.icon) {
    return pickerOption.icon;
  } else if (condition.invalid) {
    return 'ErrorIcon';
  }
  return <IconType>'';
}

function onMouseDownInside() {
  allowClickAway.value = false;
}

function onMouseUpInside() {
  allowClickAway.value = true;
}

function closeConfig() {
  if (allowClickAway.value) {
    checkConditionStillValid();
    emit('updated', condition.value!);
    isOpen.value = false;
    emit('config-closed');
  }

  allowClickAway.value = true;
}

function openConfig() {
  if (props.disabled) {
    return;
  }

  isOpen.value = true;
  emit('config-opened');
}

function updateCondition(update: ConditionViewModel) {
  condition.value = update;
}

function onConfigCompleted(update: ConditionViewModel) {
  condition.value = update;
  closeConfig();
}

function deleteCondition() {
  if (props.disabled) {
    return;
  }

  deleted.value = true;
  emit('deleted');
}

function checkConditionStillValid() {
  if (condition.value) {
    $v.value.$touch();
    const isInvalid = !props.manager.isConditionValid(condition.value) || $v.value.$invalid;

    const currentlyInvalid = !!condition.value.invalid;
    if (isInvalid !== currentlyInvalid) {
      condition.value.invalid = isInvalid;
      emit('updated', condition.value);
    }
  }
}
</script>

<style scoped lang="scss">
.condition-button-container {
  display: flex;
  position: relative;
  flex-direction: column;
}

.config-wrapper {
  position: relative;
}

.button {
  background-color: #eee;
  border: solid 1px #eee;
  color: inherit;
  min-width: 0;
  opacity: 1;

  &:hover {
    color: inherit;
    background-color: #ccc;
    border: solid 1px #ccc;
    .delete-icon {
      visibility: visible;
    }
  }

  &:focus {
    color: inherit;
    border-color: #eee;
    outline: none;
  }

  &.config-active {
    background-color: #ddd;
    border: solid 1px #ddd;
  }

  &:focus:not(:active) {
    box-shadow: none;
  }

  &.invalid {
    background: #ffd7df;
    &.config-active {
      background: #ffc3cf;
    }
  }

  &.disabled {
    opacity: 0.6;
    cursor: default;
  }

  .material-design-icon {
    margin-bottom: 2px;
  }
}

.description {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-left: 0.5em;
  max-width: 500px;
}

.delete-icon {
  position: absolute;
  right: 0;
  top: 10px;
  width: 30px;
  visibility: hidden;
  background: inherit;
}

.help.is-danger {
  display: flex;
  flex-direction: column;
  justify-content: center;
}
</style>
